import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Property {
  id: string;
  user_id: string;
  organization_id: string | null;
  title: string;
  address: string;
  city: string;
  postal_code: string | null;
  property_type: string;
  transaction_type: string;
  surface_m2: number | null;
  rooms: number | null;
  price: number | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  status: string;
  compliance_score: number;
  created_at: string;
  updated_at: string;
}

export interface PropertyDocument {
  id: string;
  property_id: string;
  document_id: string | null;
  doc_type: string;
  label: string | null;
  valid_from: string | null;
  expires_at: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  linked_document?: {
    id: string;
    title: string;
    file_name: string;
  } | null;
}

export type PropertyInsert = Omit<Property, "id" | "user_id" | "created_at" | "updated_at" | "compliance_score">;

// Types de documents immobiliers avec leurs règles
export const PROPERTY_DOC_TYPES: Record<string, {
  label: string;
  mandatory: { sale: boolean; rental: boolean };
  validityDays: number | null;
}> = {
  // Diagnostics obligatoires
  dpe: { label: "DPE (Diagnostic Performance Énergétique)", mandatory: { sale: true, rental: true }, validityDays: 3650 },
  diagnostic_amiante: { label: "Diagnostic Amiante", mandatory: { sale: true, rental: false }, validityDays: null },
  diagnostic_plomb: { label: "Diagnostic Plomb (CREP)", mandatory: { sale: true, rental: true }, validityDays: null },
  diagnostic_termites: { label: "Diagnostic Termites", mandatory: { sale: true, rental: false }, validityDays: 180 },
  diagnostic_electricite: { label: "Diagnostic Électricité", mandatory: { sale: true, rental: true }, validityDays: 1095 },
  diagnostic_gaz: { label: "Diagnostic Gaz", mandatory: { sale: true, rental: true }, validityDays: 1095 },
  erp: { label: "ERP (État des Risques et Pollutions)", mandatory: { sale: true, rental: true }, validityDays: 180 },
  mesurage_carrez: { label: "Mesurage Loi Carrez", mandatory: { sale: true, rental: false }, validityDays: null },
  
  // Documents de propriété
  titre_propriete: { label: "Titre de propriété", mandatory: { sale: true, rental: false }, validityDays: null },
  
  // Copropriété
  pv_ag: { label: "PV des 3 dernières AG", mandatory: { sale: true, rental: false }, validityDays: null },
  reglement_copro: { label: "Règlement de copropriété", mandatory: { sale: true, rental: false }, validityDays: null },
  carnet_entretien: { label: "Carnet d'entretien", mandatory: { sale: true, rental: false }, validityDays: null },
  
  // Location spécifique
  bail: { label: "Bail / Contrat de location", mandatory: { sale: false, rental: true }, validityDays: null },
  etat_lieux_entree: { label: "État des lieux d'entrée", mandatory: { sale: false, rental: true }, validityDays: null },
  attestation_assurance: { label: "Attestation d'assurance locataire", mandatory: { sale: false, rental: true }, validityDays: 365 },
  
  // Transaction
  mandat: { label: "Mandat de vente/location", mandatory: { sale: true, rental: true }, validityDays: null },
  compromis: { label: "Compromis de vente", mandatory: { sale: true, rental: false }, validityDays: null },
  acte_authentique: { label: "Acte authentique", mandatory: { sale: true, rental: false }, validityDays: null },
  
  // Autres
  photos: { label: "Photos du bien", mandatory: { sale: false, rental: false }, validityDays: null },
  autres: { label: "Autres documents", mandatory: { sale: false, rental: false }, validityDays: null },
};

export function getRequiredDocs(transactionType: "sale" | "rental"): string[] {
  return Object.entries(PROPERTY_DOC_TYPES)
    .filter(([_, config]) => config.mandatory[transactionType])
    .map(([key]) => key);
}

export function calculateComplianceScore(
  docs: PropertyDocument[],
  transactionType: "sale" | "rental"
): number {
  const required = getRequiredDocs(transactionType);
  if (required.length === 0) return 100;
  
  const validDocs = docs.filter(
    (d) => required.includes(d.doc_type) && d.status === "valid"
  );
  
  return Math.round((validDocs.length / required.length) * 100);
}

export function useProperties() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: properties = [], isLoading, error } = useQuery({
    queryKey: ["properties", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Property[];
    },
    enabled: !!user?.id,
  });

  const createProperty = useMutation({
    mutationFn: async (property: PropertyInsert) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("properties")
        .insert({
          ...property,
          user_id: user.id,
          compliance_score: 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Property;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("Bien ajouté avec succès");
    },
    onError: (error) => {
      console.error("Error creating property:", error);
      toast.error("Erreur lors de l'ajout du bien");
    },
  });

  const updateProperty = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Property> & { id: string }) => {
      const { data, error } = await supabase
        .from("properties")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Property;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("Bien mis à jour");
    },
    onError: (error) => {
      console.error("Error updating property:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  const deleteProperty = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("properties")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("Bien supprimé");
    },
    onError: (error) => {
      console.error("Error deleting property:", error);
      toast.error("Erreur lors de la suppression");
    },
  });

  return {
    properties,
    isLoading,
    error,
    createProperty,
    updateProperty,
    deleteProperty,
  };
}

export function usePropertyDocuments(propertyId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["property-documents", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];

      const { data, error } = await supabase
        .from("property_documents")
        .select(`
          *,
          linked_document:documents(id, title, file_name)
        `)
        .eq("property_id", propertyId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as PropertyDocument[];
    },
    enabled: !!propertyId,
  });

  const addDocument = useMutation({
    mutationFn: async (doc: Omit<PropertyDocument, "id" | "created_at" | "linked_document">) => {
      const { data, error } = await supabase
        .from("property_documents")
        .insert(doc)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-documents", propertyId] });
      toast.success("Document ajouté");
    },
    onError: (error) => {
      console.error("Error adding document:", error);
      toast.error("Erreur lors de l'ajout");
    },
  });

  const updateDocument = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PropertyDocument> & { id: string }) => {
      const { data, error } = await supabase
        .from("property_documents")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-documents", propertyId] });
    },
  });

  const linkDocument = useMutation({
    mutationFn: async ({ docId, documentId }: { docId: string; documentId: string }) => {
      const { error } = await supabase
        .from("property_documents")
        .update({ 
          document_id: documentId,
          status: "valid"
        })
        .eq("id", docId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-documents", propertyId] });
      toast.success("Document lié avec succès");
    },
    onError: (error) => {
      console.error("Error linking document:", error);
      toast.error("Erreur lors de la liaison");
    },
  });

  const deleteDocument = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("property_documents")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-documents", propertyId] });
      toast.success("Document supprimé");
    },
  });

  return {
    documents,
    isLoading,
    addDocument,
    updateDocument,
    linkDocument,
    deleteDocument,
  };
}
